import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import ConfirmDialog from './ConfirmDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyForm = {
  name: '',
  role: 'Persönlicher Berater',
  email: '',
  phone: '',
  hours: 'Mo–Fr 10–18 Uhr',
  photo_url: '',
  active: true,
};

const photoSrc = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  if (url.startsWith('/')) return `${BACKEND_URL}${url}`;
  return url;
};

const AdminAdvisors = () => {
  const [advisors, setAdvisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/advisors`);
      setAdvisors(res.data || []);
    } catch (e) {
      toast.error('Berater konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (adv) => {
    setEditing(adv);
    setForm({
      name: adv.name || '',
      role: adv.role || 'Persönlicher Berater',
      email: adv.email || '',
      phone: adv.phone || '',
      hours: adv.hours || '',
      photo_url: adv.photo_url || '',
      active: !!adv.active,
    });
    setShowModal(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Name ist erforderlich');
      return;
    }
    setSaving(true);
    try {
      let advisorId = editing?.id;
      if (editing) {
        await axios.patch(`${API}/admin/advisors/${editing.id}`, form);
      } else {
        const res = await axios.post(`${API}/admin/advisors`, form);
        advisorId = res.data?.id;
      }
      toast.success(editing ? 'Berater aktualisiert' : 'Berater angelegt');
      setShowModal(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Nur JPG, PNG oder WEBP');
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error('Bild zu groß (max. 4 MB)');
      return;
    }
    // If new (no id yet), save advisor first
    let advisorId = editing?.id;
    if (!advisorId) {
      if (!form.name.trim()) {
        toast.error('Bitte erst Name ausfüllen, dann Foto hochladen');
        return;
      }
      try {
        const res = await axios.post(`${API}/admin/advisors`, form);
        advisorId = res.data?.id;
        setEditing(res.data);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Anlage fehlgeschlagen');
        return;
      }
    }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/admin/advisors/${advisorId}/photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm((f) => ({ ...f, photo_url: res.data.photo_url }));
      toast.success('Foto hochgeladen');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const remove = (adv) => {
    setConfirm({
      title: `Berater "${adv.name}" löschen?`,
      message: adv.assigned_customers
        ? `${adv.assigned_customers} zugewiesene Kunden werden ohne Berater zurückbleiben.`
        : 'Dieser Schritt kann nicht rückgängig gemacht werden.',
      confirmLabel: 'Endgültig löschen',
      danger: true,
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/admin/advisors/${adv.id}`);
          toast.success('Berater gelöscht');
          load();
        } catch (e) {
          toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen');
        }
      },
    });
  };

  return (
    <div data-testid="admin-advisors-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-heading font-bold">Berater</h2>
          <p className="text-sm text-muted-foreground mt-1">Verwaltung der persönlichen Berater</p>
        </div>
        <button onClick={openCreate} className="btn-primary" data-testid="advisor-create-button">
          + Neuer Berater
        </button>
      </div>

      {loading ? (
        <div className="card text-center py-12 text-muted-foreground">Lade …</div>
      ) : advisors.length === 0 ? (
        <div className="card text-center py-12">
          <p className="text-muted-foreground mb-4">Noch keine Berater angelegt</p>
          <button onClick={openCreate} className="btn-primary" data-testid="advisor-empty-create">Ersten Berater anlegen</button>
        </div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b">
              <tr>
                <th className="py-3 pr-4">Berater</th>
                <th className="py-3 pr-4">Kontakt</th>
                <th className="py-3 pr-4">Verfügbarkeit</th>
                <th className="py-3 pr-4 text-right">Kunden</th>
                <th className="py-3 pr-4">Status</th>
                <th className="py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {advisors.map((a) => (
                <tr key={a.id} data-testid={`advisor-row-${a.id}`}>
                  <td className="py-3 pr-4">
                    <div className="flex items-center gap-3">
                      {a.photo_url ? (
                        <img src={photoSrc(a.photo_url)} alt={a.name} className="w-9 h-9 rounded-full object-cover" />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-accent/15 text-accent flex items-center justify-center font-medium">
                          {a.name.split(' ').filter(Boolean).slice(0,2).map(p=>p[0]).join('')}
                        </div>
                      )}
                      <div>
                        <div className="font-medium">{a.name}</div>
                        <div className="text-xs text-muted-foreground">{a.role}</div>
                      </div>
                    </div>
                  </td>
                  <td className="py-3 pr-4">
                    <div>{a.email || <span className="text-muted-foreground">—</span>}</div>
                    <div className="text-xs text-muted-foreground tabular-nums">{a.phone || '—'}</div>
                  </td>
                  <td className="py-3 pr-4 text-xs">{a.hours || '—'}</td>
                  <td className="py-3 pr-4 text-right tabular-nums">{a.assigned_customers}</td>
                  <td className="py-3 pr-4">
                    {a.active ? (
                      <span className="inline-flex px-2 py-0.5 rounded-full bg-green-100 text-green-800 text-xs">aktiv</span>
                    ) : (
                      <span className="inline-flex px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs">inaktiv</span>
                    )}
                  </td>
                  <td className="py-3 text-right whitespace-nowrap">
                    <button onClick={() => openEdit(a)} className="text-xs px-3 py-1 bg-secondary hover:bg-gray-200 rounded mr-2" data-testid={`advisor-edit-${a.id}`}>Bearbeiten</button>
                    <button onClick={() => remove(a)} className="text-xs px-3 py-1 bg-red-50 text-red-700 hover:bg-red-100 rounded" data-testid={`advisor-delete-${a.id}`}>Löschen</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 vp-anim-fade" onClick={() => !saving && !uploading && setShowModal(false)}>
          <form onClick={(e)=>e.stopPropagation()} onSubmit={save} className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto vp-anim-scale" data-testid="advisor-modal">
            <h3 className="text-lg font-heading font-bold">{editing ? 'Berater bearbeiten' : 'Neuer Berater'}</h3>

            <div className="flex items-center gap-4">
              {form.photo_url ? (
                <img src={photoSrc(form.photo_url)} alt="Vorschau" className="w-20 h-20 rounded-full object-cover ring-1 ring-gray-200" />
              ) : (
                <div className="w-20 h-20 rounded-full bg-accent/15 text-accent flex items-center justify-center font-medium text-lg">
                  {form.name.split(' ').filter(Boolean).slice(0,2).map(p=>p[0]).join('') || '—'}
                </div>
              )}
              <div className="flex-1">
                <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onFileChange} className="hidden" data-testid="advisor-photo-input" />
                <button type="button" onClick={()=>fileInputRef.current?.click()} disabled={uploading} className="text-sm px-3 py-1.5 bg-secondary hover:bg-gray-200 rounded mr-2" data-testid="advisor-photo-upload-button">
                  {uploading ? 'Lädt hoch …' : (form.photo_url ? 'Anderes Foto wählen' : 'Foto hochladen')}
                </button>
                {form.photo_url && (
                  <button type="button" onClick={()=>setForm({...form, photo_url:''})} className="text-sm text-red-600 hover:underline" data-testid="advisor-photo-remove">
                    Entfernen
                  </button>
                )}
                <p className="text-[11px] text-muted-foreground mt-1">JPG, PNG oder WEBP · max. 4 MB</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Name *</label>
              <input type="text" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} className="w-full px-3 py-2 border rounded" required data-testid="advisor-input-name" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Rolle / Titel</label>
              <input type="text" value={form.role} onChange={(e)=>setForm({...form, role:e.target.value})} className="w-full px-3 py-2 border rounded" data-testid="advisor-input-role" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">E-Mail</label>
                <input type="email" value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} className="w-full px-3 py-2 border rounded" data-testid="advisor-input-email" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Telefon</label>
                <input type="text" value={form.phone} onChange={(e)=>setForm({...form, phone:e.target.value})} className="w-full px-3 py-2 border rounded" data-testid="advisor-input-phone" />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Verfügbarkeit</label>
              <input type="text" value={form.hours} onChange={(e)=>setForm({...form, hours:e.target.value})} placeholder="z.B. Mo–Fr 10–18 Uhr" className="w-full px-3 py-2 border rounded" data-testid="advisor-input-hours" />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.active} onChange={(e)=>setForm({...form, active:e.target.checked})} data-testid="advisor-input-active" />
              Aktiv (in Auswahllisten anzeigen)
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={()=>setShowModal(false)} className="btn-secondary" disabled={saving || uploading}>Abbrechen</button>
              <button type="submit" className="btn-primary" disabled={saving || uploading} data-testid="advisor-save-button">
                {saving ? 'Speichert …' : (editing ? 'Speichern' : 'Anlegen')}
              </button>
            </div>
          </form>
        </div>
      )}

      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
};

export default AdminAdvisors;
